import { useEffect, useRef, useState } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { Guide, Step, Supply } from './types';
import { NoImageThumb } from './NoImageThumb';

interface RailProps {
   guide: Guide;
   // 0 = intro, 1..N = steps, N+1 = conclusion.
   index: number;
   totalSteps: number;
   // Mobile drawer open (full-screen slide-in below the breakpoint).
   open: boolean;
   // Desktop docked column hidden (the header toggle collapses it ≥ breakpoint).
   collapsed: boolean;
   onJump: (step: number) => void;
}

export interface TocSection {
   title: string;
   // First step the section covers; 0 for the introduction.
   step: number;
   // Last step it covers — untitled steps fold into the section above them.
   lastStep: number;
}

/**
 * Click-to-jump section list plus the guide's tools and parts: a docked 260px
 * column on wider embeds, a full-screen drawer on narrow ones.
 */
export function Rail({ guide, index, totalSteps, open, collapsed, onJump }: RailProps) {
   const { sections, numbered } = buildToc(guide.steps);
   const activeStep = activeSectionStep(sections, index);
   const navRef = useRef<HTMLElement>(null);
   // On the intro, tools/parts already show in the intro's own section, so the
   // rail copy stays hidden until the reader navigates into the steps.
   const showSupplies = index > 0 && (guide.tools.length > 0 || guide.parts.length > 0);

   // A fifty-step guide scrolls its own list: without this, paging past the
   // visible rows leaves the highlight somewhere off-screen.
   useEffect(() => {
      const row = navRef.current?.querySelector('[aria-current]');
      // Optional call because jsdom's Element has no scrollIntoView.
      row?.scrollIntoView?.({ block: 'nearest' });
      // The supplies below settle after this commit and shorten the scroller,
      // which left the row hanging 14px past its new bottom edge.
      const frame = requestAnimationFrame(() => row?.scrollIntoView?.({ block: 'nearest' }));
      return () => cancelAnimationFrame(frame);
   }, [activeStep]);

   return (
      <aside
         className={cn(
            'fixed inset-y-0 left-0 z-30 flex w-full flex-col overflow-hidden bg-gray-50 transition-[transform,visibility] duration-200 motion-reduce:transition-none md:w-[260px] md:border-r md:border-gray-200',
            // Off screen is not out of the tab order; visibility is. It rides
            // the transition so the slide out still plays.
            open ? 'visible translate-x-0' : 'invisible -translate-x-full',
            collapsed ? 'md:invisible md:-translate-x-full' : 'md:visible md:translate-x-0'
         )}
         aria-label={_js('Guide steps')}
      >
         {/* pt-1 below the breakpoint so the drawer's Back row sits in the same
             band as the header bar it covers; the docked column keeps pt-4. */}
         <div className="flex h-full flex-col px-4 pb-4 pt-1 md:pt-4">
            <button
               type="button"
               onClick={() => onJump(index)}
               className="button-ghost -mx-4 mb-2 min-h-11 justify-start px-4 text-base font-semibold text-gray-700 md:hidden"
            >
               <FaIcon name="arrow-left" aria-hidden />
               {_js('Back')}
            </button>

            {/* The header carries the progress bar; the drawer covers it on
                mobile, so the rail keeps the reading in words. */}
            <span className="mb-3 flex-none text-sm font-semibold text-gray-500">
               {progressLabel(index, totalSteps)}
            </span>

            <nav
               ref={navRef}
               // py-1 so the selected row's ring and a focus ring aren't
               // clipped by this scroller at the first and last rows.
               className="embed-scroll -mx-3.5 min-h-20 flex-1 overflow-y-auto px-3.5 py-1"
               aria-label={_js('Guide steps')}
            >
               <ul className="m-0 list-none p-0">
                  {sections.map(section => (
                     <li key={section.step} className="[&+li]:mt-1">
                        <button
                           type="button"
                           data-step={section.step}
                           onClick={() => onJump(section.step)}
                           aria-current={section.step === activeStep ? 'step' : undefined}
                           className={cn(
                              'flex w-full items-start gap-2.5 rounded-lg border px-2.5 py-2 text-left text-sm font-semibold leading-[18px]',
                              'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                              // Both rings sit outside the border box, and the
                              // scroller's padding only clears them at the ends
                              // of its range. scrollIntoView reads this instead.
                              'scroll-my-1',
                              // DESIGN.md's Ring-Not-Fill rule: a gray-filled
                              // row would read as disabled.
                              section.step === activeStep
                                 ? 'border-brand-500 bg-white text-brand-700 ring-1 ring-brand-500'
                                 : 'border-transparent text-gray-700 hover:bg-gray-200'
                           )}
                        >
                           {numbered && (
                              // Spans, not single numbers: every step the header
                              // can name has to land on a row here.
                              <span
                                 aria-hidden
                                 className="w-10 shrink-0 pt-px text-right text-xs tabular-nums text-gray-500"
                              >
                                 {stepRange(section)}
                              </span>
                           )}
                           <span className="min-w-0 flex-1 break-words">{section.title}</span>
                        </button>
                     </li>
                  ))}
               </ul>
            </nav>

            {showSupplies && <SupplyDrawer guide={guide} />}
         </div>
      </aside>
   );
}

/**
 * Tools and parts under the step list. Collapsible because on a long guide the
 * supply list otherwise takes half the rail from the steps it sits beside.
 */
function SupplyDrawer({ guide }: { guide: Guide }) {
   const [open, setOpen] = useState(true);
   const count = guide.tools.length + guide.parts.length;
   const groups = [
      { title: _js('Tools'), items: guide.tools, icon: 'screwdriver-wrench' },
      { title: _js('Parts'), items: guide.parts, icon: 'gear' },
   ].filter(group => group.items.length > 0);
   // A parts-only guide was still labelled Parts beside the tools icon.
   const icon = groups.length === 1 ? groups[0].icon : 'screwdriver-wrench';
   // Joins the catalogued strings rather than minting "Tools & Parts", which
   // would read English until the next extraction round.
   const label = groups.map(group => group.title).join(' · ');

   return (
      <div className="-mx-3.5 -mb-4 mt-2.5 flex max-h-[45%] flex-none flex-col border-t border-gray-300 bg-white px-3.5">
         <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="button-ghost h-11 shrink-0 justify-start gap-2 px-1 text-sm font-semibold text-gray-700"
         >
            <FaIcon name={icon} aria-hidden />
            <span>{label}</span>
            <span className="tabular-nums text-gray-500">{count}</span>
            <FaIcon
               name="chevron-down"
               className={cn(
                  'ms-auto text-gray-500 transition-transform motion-reduce:transition-none',
                  open && 'rotate-180'
               )}
               aria-hidden
            />
         </button>

         {open && (
            <div className="embed-scroll min-h-0 flex-1 overflow-y-auto pb-4">
               {groups.map(group => (
                  <SupplyGroup
                     key={group.title}
                     title={group.title}
                     items={group.items}
                     // One group is already named by the toggle above it.
                     showHeading={groups.length > 1}
                  />
               ))}
            </div>
         )}
      </div>
   );
}

function SupplyGroup({
   title,
   items,
   showHeading,
}: {
   title: string;
   items: Supply[];
   showHeading: boolean;
}) {
   return (
      <div className="[&+&]:mt-3.5">
         {showHeading && <h3 className="m-0 mb-2 text-xs font-semibold text-gray-500">{title}</h3>}
         <ul className="m-0 list-none p-0">
            {items.map((item, i) => (
               <li key={i} className="mb-0.5">
                  <SupplyRow item={item} />
               </li>
            ))}
         </ul>
      </div>
   );
}

function SupplyRow({ item }: { item: Supply }) {
   const inner = (
      <>
         {item.thumbnail ? (
            <img
               className="size-11 flex-none rounded-lg border border-gray-200 bg-white object-contain"
               src={item.thumbnail}
               alt=""
               width={44}
               height={44}
               loading="lazy"
            />
         ) : (
            <NoImageThumb className="size-11" />
         )}
         <span className="min-w-0 flex-1 break-words text-sm leading-4">{item.text}</span>
         {item.quantity > 1 && (
            <span className="flex-none text-sm font-semibold text-gray-500">×{item.quantity}</span>
         )}
      </>
   );

   const className =
      'flex items-center gap-3 rounded-lg p-1.5 text-gray-700 no-underline transition-colors';

   // Supply links point out to ifixit.com; open them in the parent window.
   return item.url ? (
      <a
         className={cn(className, 'hover:bg-gray-100 [&:hover_span]:text-brand-500')}
         href={item.url}
         target="_blank"
         rel="noreferrer"
      >
         {inner}
      </a>
   ) : (
      <div className={className}>{inner}</div>
   );
}

function stepRange({ step, lastStep }: TocSection): string {
   if (step === 0) {
      return '';
   }
   return step === lastStep ? String(step) : `${step}-${lastStep}`;
}

/**
 * Section headings, with the untitled steps after one folded into it. A guide
 * with no titled steps falls back to rows that name their own number.
 */
export function buildToc(steps: Step[]): { sections: TocSection[]; numbered: boolean } {
   const sections: TocSection[] = [{ title: _js('Introduction'), step: 0, lastStep: 0 }];

   for (const [index, step] of steps.entries()) {
      const title = step.title?.trim();
      if (title) {
         sections.push({ title, step: index + 1, lastStep: index + 1 });
      }
   }

   if (sections.length === 1) {
      for (const index of steps.keys()) {
         const step = index + 1;
         sections.push({ title: _js('Step %1', String(step)), step, lastStep: step });
      }
      return { sections, numbered: false };
   }

   for (const [i, section] of sections.entries()) {
      const next = sections[i + 1];
      section.lastStep = next ? next.step - 1 : steps.length;
   }
   // The introduction is a row of its own, never a span over the steps.
   sections[0].lastStep = 0;

   return { sections, numbered: true };
}

// The TOC lists only section headings, so an untitled step highlights the
// section it falls under: the entry with the greatest step ≤ index.
function activeSectionStep(sections: TocSection[], index: number): number | null {
   let active: number | null = null;
   for (const section of sections) {
      if (section.step <= index && (active === null || section.step > active)) {
         active = section.step;
      }
   }
   return active;
}

function progressLabel(index: number, totalSteps: number): string {
   if (index <= 0) {
      return _js('Introduction');
   }
   if (index > totalSteps) {
      return _js('Complete');
   }
   return _js('%1 of %2', String(index), String(totalSteps));
}
